import { useState, useEffect, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useMarketDataRefreshInterval } from '../hooks/useMarketDataRefreshInterval'
import { useNotification } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import { calcPositionRisk, calcPositionRiskPercent } from '../lib/tradeCalculations'
import { formatTimeToOpenForTooltip } from '../lib/formatMarketDuration'
import type { Trade } from '../types/trade'
import TradeListQuickActions from './TradeListQuickActions'
import TradeQuickReviewModal from './TradeQuickReviewModal'
import DashboardWidgetTitle from './DashboardWidgetTitle'

type SymbolMeta = { exchange: string | null; mic_code: string | null }

type ExchangeMarketState = {
  is_market_open: boolean
  time_to_open: string | null
}

function buildSymbolMetaMap(
  symbolsData: Array<{ symbol: string; market: string; exchange: string | null; mic_code: string | null }>
): Record<string, SymbolMeta> {
  const map: Record<string, SymbolMeta> = {}
  for (const s of symbolsData) {
    const key = `${s.market}::${s.symbol}`.toUpperCase()
    const existing = map[key]
    if (!existing || (existing.exchange == null && s.exchange != null)) {
      map[key] = { exchange: s.exchange, mic_code: s.mic_code }
    }
  }
  return map
}

function buildMarketStateMap(
  rows: Array<{
    exchange: string
    mic_code: string | null
    is_market_open: boolean | null
    time_to_open: string | null
  }>
): Record<string, ExchangeMarketState> {
  const map: Record<string, ExchangeMarketState> = {}
  for (const row of rows) {
    if (row.is_market_open == null) continue
    const state: ExchangeMarketState = {
      is_market_open: row.is_market_open,
      time_to_open: row.time_to_open,
    }
    if (row.exchange) map[row.exchange.toUpperCase()] = state
    if (row.mic_code) map[row.mic_code.toUpperCase()] = state
  }
  return map
}

function resolveMarketState(
  trade: Trade,
  symbolMetaByKey: Record<string, SymbolMeta>,
  marketStateByKey: Record<string, ExchangeMarketState>
): ExchangeMarketState | null {
  const tradeKey = `${trade.market}::${trade.symbol}`.toUpperCase()
  const meta = symbolMetaByKey[tradeKey]
  const keysToTry = [meta?.mic_code, meta?.exchange, trade.exchange].filter(
    (k): k is string => Boolean(k?.trim())
  )

  for (const k of keysToTry) {
    const normalized = k.trim().toUpperCase()
    if (normalized in marketStateByKey) return marketStateByKey[normalized]
  }
  return null
}

function buildMarketStatusTooltip(
  t: (key: string, options?: Record<string, string | number>) => string,
  state: ExchangeMarketState
): string {
  if (state.is_market_open) {
    return t('tradeList.marketOpen')
  }
  return t('tradeList.marketClosedTooltip', {
    time: formatTimeToOpenForTooltip(t, state.time_to_open),
  })
}

function MarketStatusDot({ isOpen, title }: { isOpen: boolean; title: string }) {
  return (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`}
      title={title}
      aria-label={title}
    />
  )
}

interface TradeListProps {
  refreshTrigger?: number
  /** When true, only the table body scrolls (used in dashboard widget) */
  isDashboardWidget?: boolean
  scrollContainerProps?: { style?: { overscrollBehavior?: 'contain'; touchAction?: 'pan-y' } }
  onScrollGuard?: (e: React.WheelEvent<HTMLElement>) => void
}

const ROW_TABLE_STORAGE_KEY = 'tradeList_rowVisibleColumns'

export type RowOptionalColumnId =
  | 'entry'
  | 'averageCost'
  | 'positionSize'
  | 'totalInvested'
  | 'sl'
  | 'tp'
  | 'risk'
  | 'riskPercent'
  | 'price'
  | 'pnlPercent'
  | 'pnl'
  | 'account'
  | 'exchange'
  | 'tags'

const ALL_ROW_OPTIONAL_COLUMNS: RowOptionalColumnId[] = [
  'entry',
  'averageCost',
  'positionSize',
  'totalInvested',
  'sl',
  'tp',
  'risk',
  'riskPercent',
  'price',
  'pnlPercent',
  'pnl',
  'account',
  'exchange',
  'tags',
]

function loadRowVisibleColumns(): RowOptionalColumnId[] {
  try {
    const raw = localStorage.getItem(ROW_TABLE_STORAGE_KEY)
    if (!raw) return [...ALL_ROW_OPTIONAL_COLUMNS]
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return [...ALL_ROW_OPTIONAL_COLUMNS]
    const valid = parsed.filter((id): id is RowOptionalColumnId => ALL_ROW_OPTIONAL_COLUMNS.includes(id as RowOptionalColumnId))
    return valid.length > 0 ? valid : [...ALL_ROW_OPTIONAL_COLUMNS]
  } catch {
    return [...ALL_ROW_OPTIONAL_COLUMNS]
  }
}

type EditTableModalRow = { id: RowOptionalColumnId; visible: boolean }

function buildEditTableModalRows(visibleCols: RowOptionalColumnId[]): EditTableModalRow[] {
  const set = new Set(visibleCols)
  const orderedVisible = visibleCols.filter((id) => ALL_ROW_OPTIONAL_COLUMNS.includes(id))
  const rest = ALL_ROW_OPTIONAL_COLUMNS.filter((id) => !set.has(id))
  return [...orderedVisible.map((id) => ({ id, visible: true })), ...rest.map((id) => ({ id, visible: false }))]
}

function visibleIdsFromModalRows(rows: EditTableModalRow[]): RowOptionalColumnId[] {
  return rows.filter((r) => r.visible).map((r) => r.id)
}

/** 2×3 grippedots — column edit trigger + drag handle */
function GripDotsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="9" cy="6" r="1.35" />
      <circle cx="15" cy="6" r="1.35" />
      <circle cx="9" cy="12" r="1.35" />
      <circle cx="15" cy="12" r="1.35" />
      <circle cx="9" cy="18" r="1.35" />
      <circle cx="15" cy="18" r="1.35" />
    </svg>
  )
}

interface SortableEditTableRowProps {
  row: EditTableModalRow
  label: string
  dragLabel: string
  onToggleVisible: (id: RowOptionalColumnId) => void
}

function SortableEditTableRow({ row, label, dragLabel, onToggleVisible }: SortableEditTableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 rounded-md p-1.5 hover:bg-gray-50 ${isDragging ? 'z-10 bg-white opacity-95 shadow-md ring-1 ring-gray-200' : ''}`}
    >
      <button
        type="button"
        className="touch-none cursor-grab rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={dragLabel}
      >
        <GripDotsIcon className="h-5 w-5 shrink-0" />
      </button>
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-0.5">
        <input
          type="checkbox"
          checked={row.visible}
          onChange={() => onToggleVisible(row.id)}
          className="h-4 w-4 shrink-0 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
        />
        <span className="truncate text-sm text-gray-800">{label}</span>
      </label>
    </div>
  )
}

export default function TradeList({ refreshTrigger, isDashboardWidget, scrollContainerProps, onScrollGuard }: TradeListProps) {
  const { t } = useTranslation(['trading', 'common'])
  const { user } = useAuth()
  const { membershipType } = useMembership()
  const { showError } = useNotification()
  const navigate = useNavigate()
  const [trades, setTrades] = useState<Trade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null)
  const [showOnlyOpen, setShowOnlyOpen] = useState(true)
  const [viewMode, setViewMode] = useState<'box' | 'row'>('row')
  const [rowVisibleColumns, setRowVisibleColumns] = useState<RowOptionalColumnId[]>(() => loadRowVisibleColumns())
  const [showEditTableModal, setShowEditTableModal] = useState(false)
  const [editTableModalRows, setEditTableModalRows] = useState<EditTableModalRow[] | null>(null)
  const [rowSortBy, setRowSortBy] = useState<'symbol' | RowOptionalColumnId>('entry')
  const [rowSortDir, setRowSortDir] = useState<'asc' | 'desc'>('desc')
  const [accountNamesById, setAccountNamesById] = useState<Record<string, string>>({})
  const [symbolMetaByKey, setSymbolMetaByKey] = useState<Record<string, SymbolMeta>>({})
  const [marketStateByKey, setMarketStateByKey] = useState<Record<string, ExchangeMarketState>>({})

  const editTableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleEditTableDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setEditTableModalRows((prev) => {
      if (!prev) return prev
      const oldIndex = prev.findIndex((r) => r.id === active.id)
      const newIndex = prev.findIndex((r) => r.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const toggleEditTableModalRowVisible = (id: RowOptionalColumnId) => {
    setEditTableModalRows((prev) => prev?.map((r) => (r.id === id ? { ...r, visible: !r.visible } : r)) ?? null)
  }

  const fetchTrades = useCallback(async () => {
    if (!user) {
      setTrades([])
      setSymbolMetaByKey({})
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('trades_with_market_data')
        .select('*')
        .eq('user_id', user.id)
        .order('entry_date', { ascending: false })

      if (error) throw error

      const baseTrades = (data || []) as Trade[]

      try {
        const uniqueSymbols = Array.from(new Set(baseTrades.map((t) => t.symbol).filter(Boolean)))
        const uniqueMarkets = Array.from(new Set(baseTrades.map((t) => t.market).filter(Boolean)))

        if (uniqueSymbols.length > 0) {
          let symbolsQuery = supabase
            .from('symbols')
            .select('symbol, market, exchange, mic_code')
            .in('symbol', uniqueSymbols)

          if (uniqueMarkets.length > 0) {
            symbolsQuery = symbolsQuery.in('market', uniqueMarkets)
          }

          const { data: symbolsData, error: symbolsError } = await symbolsQuery

          if (!symbolsError && symbolsData) {
            const metaMap = buildSymbolMetaMap(
              symbolsData as Array<{ symbol: string; market: string; exchange: string | null; mic_code: string | null }>
            )
            setSymbolMetaByKey(metaMap)

            const hydrated = baseTrades.map((t) => {
              const key = `${t.market}::${t.symbol}`.toUpperCase()
              const meta = metaMap[key]
              return { ...t, exchange: meta?.exchange ?? t.exchange ?? null }
            })
            setTrades(hydrated)
          } else {
            setSymbolMetaByKey({})
            setTrades(baseTrades)
          }
        } else {
          setSymbolMetaByKey({})
          setTrades(baseTrades)
        }
      } catch (hydrateErr) {
        console.warn('Failed to hydrate exchanges from symbols:', hydrateErr)
        setSymbolMetaByKey({})
        setTrades(baseTrades)
      }

      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load trades')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchTrades()
  }, [fetchTrades, refreshTrigger])

  useMarketDataRefreshInterval(fetchTrades, Boolean(user))

  useEffect(() => {
    if (!user) {
      setMarketStateByKey({})
      return
    }

    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase
        .from('exchange_sync_state')
        .select('exchange, mic_code, is_market_open, time_to_open')

      if (cancelled || error) return
      setMarketStateByKey(
        buildMarketStateMap(
          (data ?? []) as Array<{
            exchange: string
            mic_code: string | null
            is_market_open: boolean | null
            time_to_open: string | null
          }>
        )
      )
    })()

    return () => {
      cancelled = true
    }
  }, [user, refreshTrigger])

  useEffect(() => {
    if (!user) {
      setAccountNamesById({})
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.from('accounts').select('id,name,platform').eq('user_id', user.id)
      if (cancelled || error) return
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        const r = row as { id: string; name: string | null; platform: string | null }
        map[r.id] = r.name?.trim() ? r.name.trim() : r.platform?.trim() || '–'
      }
      setAccountNamesById(map)
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatPriceWithCurrency = (price: number | null, currency: string | null) => {
    if (price === null) return '-'
    const formatted = price.toFixed(2).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  const formatCurrency = (amount: number | null, currency: string | null) => {
    if (amount === null) return '-'
    const formatted = amount.toFixed(2).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  const isTradeOpen = (trade: Trade) => {
    return !trade.exit_date
  }

  const getAverageCost = (trade: Trade): number | null => {
    const qty = trade.quantity ?? 0
    if (qty <= 0) return trade.entry_price
    // Prefer cost_basis_remaining (correct after add/reduce); fallback to total_entry/exit for legacy rows
    const basis =
      trade.cost_basis_remaining ??
      (trade.long_short === 'long' ? trade.total_entry_amount : trade.total_exit_amount)
    if (basis != null && basis > 0) return basis / qty
    return trade.entry_price
  }

  const getTotalInvested = (trade: Trade): number | null => {
    const invested = trade.total_entry_amount ?? trade.position_size
    if (invested != null && invested > 0) return invested
    const qty = trade.quantity ?? 0
    const entry = trade.entry_price
    if (qty > 0 && entry > 0) return qty * entry
    return null
  }

  const getPositionRisk = (trade: Trade): number | null =>
    calcPositionRisk(trade.entry_price, trade.stop_loss, trade.quantity, trade.contract_size, trade.long_short)

  const getPositionRiskPercent = (trade: Trade): number | null =>
    calcPositionRiskPercent(
      trade.entry_price,
      trade.stop_loss,
      trade.quantity,
      trade.contract_size,
      trade.long_short,
      getTotalInvested(trade),
    )

  const columnTitle = (id: RowOptionalColumnId): string => {
    switch (id) {
      case 'entry':
        return t('tradeList.entry')
      case 'averageCost':
        return t('tradeList.averageCost')
      case 'positionSize':
        return t('tradeList.positionSize')
      case 'totalInvested':
        return t('tradeList.totalInvested')
      case 'sl':
        return t('tradeList.sl')
      case 'tp':
        return t('tradeList.tp')
      case 'risk':
        return t('tradeList.risk')
      case 'riskPercent':
        return t('tradeList.riskPercent')
      case 'price':
        return t('tradeList.price')
      case 'pnlPercent':
        return t('tradeList.pnlPercent')
      case 'pnl':
        return t('tradeList.pnl')
      case 'account':
        return t('tradeList.account')
      case 'exchange':
        return t('tradeList.exchange')
      case 'tags':
        return t('tradeList.tags')
      default:
        return ''
    }
  }

  const getRowSortValue = (
    trade: Trade,
    key: typeof rowSortBy
  ): string | number | null => {
    const isOpen = isTradeOpen(trade)
    switch (key) {
      case 'symbol':
        return trade.symbol?.toLowerCase() ?? ''
      case 'entry':
        return trade.entry_price
      case 'averageCost':
        return getAverageCost(trade) ?? 0
      case 'positionSize':
        return trade.position_size ?? (trade.quantity ?? 0) * (trade.contract_size ?? 1)
      case 'totalInvested':
        return getTotalInvested(trade) ?? 0
      case 'sl':
        return trade.stop_loss
      case 'tp':
        return trade.take_profit
      case 'risk':
        return getPositionRisk(trade) ?? 0
      case 'riskPercent':
        return getPositionRiskPercent(trade)
      case 'price':
        return isOpen ? (trade.current_price ?? 0) : (trade.exit_price ?? 0)
      case 'pnlPercent':
        return isOpen ? (trade.unrealized_pnl_percentage ?? null) : (trade.pnl_percentage ?? null)
      case 'pnl':
        return isOpen ? (trade.unrealized_pnl ?? 0) : (trade.pnl ?? 0)
      case 'account': {
        const aid = trade.account_id
        if (!aid) return ''
        const name = accountNamesById[aid]
        return name ? name.toLowerCase() : ''
      }
      case 'exchange':
        return trade.exchange?.toLowerCase() ?? ''
      case 'tags':
        return (trade.tags?.filter((x): x is string => Boolean(x?.trim())).join(', ') ?? '').toLowerCase()
      default:
        return null
    }
  }

  const handleRowSort = (key: 'symbol' | RowOptionalColumnId) => {
    if (rowSortBy === key) {
      setRowSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setRowSortBy(key)
      setRowSortDir('desc')
    }
  }

  const effectiveShowOnlyOpen = isDashboardWidget ? true : showOnlyOpen
  const effectiveViewMode = isDashboardWidget ? 'row' : viewMode

  const filteredTradesForRow = trades.filter((trade) => !effectiveShowOnlyOpen || isTradeOpen(trade))
  const sortedTradesForRow = [...filteredTradesForRow].sort((a, b) => {
    const va = getRowSortValue(a, rowSortBy)
    const vb = getRowSortValue(b, rowSortBy)
    if (va === null && vb === null) return 0
    if (va === null) return rowSortDir === 'asc' ? -1 : 1
    if (vb === null) return rowSortDir === 'asc' ? 1 : -1
    const cmp = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : (va as number) - (vb as number)
    return rowSortDir === 'asc' ? cmp : -cmp
  })

  const createDetailId = (trade: Trade) => {
    const uuid = crypto.randomUUID ? crypto.randomUUID() : 
      `detail-${Math.random().toString(36).slice(2, 10)}`
    return `${trade.symbol}-${uuid}`
  }

  const seededAllowHistory = (seed: string) => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash << 5) - hash + seed.charCodeAt(i)
      hash |= 0
    }
    const ratio = (hash >>> 0) / 4294967295
    return ratio < 0.2
  }

  const buildDetailState = (trade: Trade) => {
    const { id: _tradeId, ...restWithUser } = trade as Trade & { user_id?: string }
    const { user_id: _userId, ...tradePayload } = restWithUser

    const baseDate = trade.entry_date ? new Date(trade.entry_date) : new Date()
    const entryPrice = trade.entry_price
    const exitPrice =
      trade.exit_price ??
      (trade.long_short === 'long' ? entryPrice * 1.02 : entryPrice * 0.98)

    const timeline = [
      {
        date: baseDate.toISOString(),
        title: 'Trade Opened',
        description: 'Initial entry placed; alert triggered on setup validation.',
        price: entryPrice,
        tag: 'entry',
      },
      {
        date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
        title: 'Daily Review',
        description: 'Reviewed stop placement and kept risk constant.',
        price: entryPrice * (trade.long_short === 'long' ? 1.01 : 0.99),
        tag: 'review',
      },
      {
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Market Event',
        description: 'Macro headline created volatility; kept discipline.',
        tag: 'macro',
      },
      {
        date: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        title: 'Adjustment',
        description: 'Moved stop to breakeven after partial target touched.',
        price: entryPrice,
        tag: 'risk',
      },
      {
        date: new Date(baseDate.getTime() + 4 * 24 * 60 * 60 * 1000).toISOString(),
        title: trade.exit_date ? 'Trade Closed' : 'Current Status',
        description: trade.exit_date
          ? 'Position exited; captured planned move.'
          : 'Position still active; monitoring key levels.',
        price: exitPrice,
        tag: trade.exit_date ? 'exit' : 'open',
      },
    ]

    const comments = [
      {
        author: 'Self-Note',
        text: 'Keep journaling intraday emotions; avoid revenge trades.',
        date: baseDate.toISOString(),
        mood: 'Focused',
      },
      {
        author: 'Market',
        text: 'Watch for liquidity sweep near prior highs before adding size.',
        date: new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        mood: 'Alert',
      },
    ]

    return {
      trade: tradePayload,
      timeline,
      comments,
      mood: 'Focused',
      tradeId: trade.id,
    }
  }

  const handleNavigateToDetail = (trade: Trade, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const detailId = createDetailId(trade)
    const detailState = buildDetailState(trade)
    const historyAccessGranted =
      membershipType === 'pro'
        ? true
        : membershipType === 'free'
          ? seededAllowHistory(detailId)
          : false

    navigate(`/app/trade/${detailId}`, { state: { ...detailState, historyAccessGranted } })
  }

  if (!user) {
    return (
      <div className="shadow-sm border border-gray-200 rounded-2xl p-6" style={{backgroundColor: '#FBFBFB'}}>
        <p className="text-gray-500 text-center">{t('tradeList.signInRequired')}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="shadow-sm border border-gray-200 rounded-2xl p-6" style={{backgroundColor: '#FBFBFB'}}>
        <p className="text-gray-500 text-center">{t('tradeList.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="shadow-sm border border-gray-200 rounded-2xl p-6" style={{backgroundColor: '#FBFBFB'}}>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className={`shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6 ${isDashboardWidget ? 'h-full min-h-0 min-w-0 flex flex-col overflow-hidden' : ''}`}
        style={{backgroundColor: '#FBFBFB'}}
      >
        {isDashboardWidget && (
          <div className="mb-2 shrink-0 pr-10">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
              <DashboardWidgetTitle widgetId="tradeList" />
            </p>
          </div>
        )}
        {!isDashboardWidget && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-xl font-semibold">{t('tradeList.title')}</h2>
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto justify-end sm:justify-end">
              {viewMode === 'row' && (
                <button
                  type="button"
                  onClick={() => {
                    setEditTableModalRows(buildEditTableModalRows(rowVisibleColumns))
                    setShowEditTableModal(true)
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('tradeList.editTable')}
                </button>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyOpen}
                  onChange={(e) => setShowOnlyOpen(e.target.checked)}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                />
                <span className="text-xs md:text-sm text-gray-700">{t('tradeList.showOnlyOpen')}</span>
              </label>
              <div
                className="flex rounded-lg border border-gray-200 overflow-hidden bg-gray-50"
                role="group"
                aria-label={t('tradeList.viewAsBox')}
              >
                <button
                  type="button"
                  onClick={() => setViewMode('box')}
                  title={t('tradeList.viewAsBox')}
                  className={`p-2 transition-colors ${
                    viewMode === 'box'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('row')}
                  title={t('tradeList.viewAsRow')}
                  className={`p-2 transition-colors ${
                    viewMode === 'row'
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
              <span className="text-xs md:text-sm text-gray-500 ml-auto sm:ml-0">
                {trades.filter((trade) => !showOnlyOpen || isTradeOpen(trade)).length}{' '}
                {trades.filter((trade) => !showOnlyOpen || isTradeOpen(trade)).length === 1
                  ? 'trade'
                  : 'trades'}
              </span>
            </div>
          </div>
        )}

        {trades.length === 0 ? (
          // Empty state with prominent Add Trade box
          <div className="flex flex-col items-center justify-center py-12">
            {/* Prominent Add Trade Box */}
            <div
              onClick={() => navigate('/app/add-trade')}
              className="relative border-4 border-orange-400 border-dashed rounded-2xl p-12 cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] w-full max-w-md animate-pulse"
              style={{backgroundColor: '#FFFBF5'}}
            >
              <div className="text-center">
                <svg
                  className="w-20 h-20 text-orange-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <div className="text-xl font-bold text-orange-600 mb-2">Add Your First Trade</div>
                <div className="text-sm text-gray-600">Click here to start tracking your trades</div>
              </div>
            </div>
            <div className="text-center py-6 mt-4">
              <p className="text-gray-500">{t('tradeList.noTrades')}. {t('tradeList.getStartedMessage')}</p>
            </div>
          </div>
        ) : effectiveViewMode === 'row' ? (
          // Row layout: sortable table (columns from rowVisibleColumns)
          <div
            className={`rounded-lg border border-gray-200 ${isDashboardWidget ? 'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white' : ''}`}
          >
            {isDashboardWidget && (
              <button
                type="button"
                title={t('tradeList.editTable')}
                aria-label={t('tradeList.editTable')}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditTableModalRows(buildEditTableModalRows(rowVisibleColumns))
                  setShowEditTableModal(true)
                }}
                className="absolute right-2 top-2 z-[30] inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
              >
                <GripDotsIcon className="h-5 w-5" />
              </button>
            )}
            <div
              className={isDashboardWidget ? 'min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto' : 'overflow-x-auto'}
              {...(isDashboardWidget && scrollContainerProps)}
              {...(isDashboardWidget && onScrollGuard && { onWheel: onScrollGuard })}
            >
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className={`bg-gray-50 ${isDashboardWidget ? 'sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]' : ''}`}>
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                    onClick={() => handleRowSort('symbol')}
                  >
                    <span className="inline-flex items-center gap-1">
                      {t('tradeList.symbol')}
                      {rowSortBy === 'symbol' && (rowSortDir === 'asc' ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      ))}
                    </span>
                  </th>
                  {rowVisibleColumns.map((key) => (
                    <th
                      key={key}
                      scope="col"
                      className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleRowSort(key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {columnTitle(key)}
                        {rowSortBy === key && (rowSortDir === 'asc' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        ))}
                      </span>
                    </th>
                  ))}
                  <th
                    scope="col"
                    aria-label={t('tradeList.actions')}
                    className={`px-3 py-2 ${isDashboardWidget ? 'pr-12' : ''}`}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedTradesForRow.map((trade) => {
                  const isOpen = isTradeOpen(trade)
                  const avgCost = getAverageCost(trade)
                  const positionSizeVal = trade.position_size ?? (trade.quantity != null && trade.contract_size != null ? trade.quantity * trade.contract_size : null)
                  const priceVal = isOpen ? trade.current_price : trade.exit_price
                  const pctVal = isOpen ? trade.unrealized_pnl_percentage : trade.pnl_percentage
                  const pnlVal = isOpen ? trade.unrealized_pnl : trade.pnl
                  const renderCell = (key: RowOptionalColumnId) => {
                    switch (key) {
                      case 'entry':
                        return formatPriceWithCurrency(trade.entry_price, trade.currency)
                      case 'averageCost':
                        return avgCost != null ? formatPriceWithCurrency(avgCost, trade.currency) : '–'
                      case 'positionSize':
                        return positionSizeVal != null ? formatCurrency(positionSizeVal, trade.currency) : '–'
                      case 'totalInvested': {
                        const invested = getTotalInvested(trade)
                        return invested != null ? formatCurrency(invested, trade.currency) : '–'
                      }
                      case 'sl':
                        return formatPriceWithCurrency(trade.stop_loss, trade.currency)
                      case 'tp':
                        return formatPriceWithCurrency(trade.take_profit, trade.currency)
                      case 'risk': {
                        const riskVal = getPositionRisk(trade)
                        return riskVal != null ? formatCurrency(riskVal, trade.currency) : '–'
                      }
                      case 'riskPercent': {
                        const riskPct = getPositionRiskPercent(trade)
                        return riskPct != null ? `${riskPct.toFixed(2).replace('.', ',')}%` : '–'
                      }
                      case 'price':
                        return priceVal != null ? formatPriceWithCurrency(priceVal, trade.currency) : '–'
                      case 'pnlPercent':
                        return pctVal != null ? (
                          <span className={pctVal >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            {pctVal >= 0 ? '+' : ''}{pctVal.toFixed(2).replace('.', ',')}%
                          </span>
                        ) : '–'
                      case 'pnl':
                        return pnlVal != null ? (
                          <span className={pnlVal >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {formatCurrency(pnlVal, trade.currency)}
                          </span>
                        ) : '–'
                      case 'account':
                        return trade.account_id ? (accountNamesById[trade.account_id] ?? '–') : '–'
                      case 'exchange':
                        return trade.exchange?.trim() ? trade.exchange : '–'
                      case 'tags': {
                        const tagList = trade.tags?.filter((x): x is string => Boolean(x?.trim())) ?? []
                        if (tagList.length === 0) return '–'
                        return (
                          <span className="inline-flex flex-wrap gap-1 align-middle">
                            {tagList.map((tag, ti) => (
                              <span key={`${tag}-${ti}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-800">
                                {tag}
                              </span>
                            ))}
                          </span>
                        )
                      }
                      default:
                        return '–'
                    }
                  }
                  return (
                    <tr
                      key={trade.id}
                      onClick={() => setSelectedTrade(trade)}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          {(() => {
                            const marketState = resolveMarketState(trade, symbolMetaByKey, marketStateByKey)
                            if (marketState === null) return null
                            return (
                              <MarketStatusDot
                                isOpen={marketState.is_market_open}
                                title={buildMarketStatusTooltip(t, marketState)}
                              />
                            )
                          })()}
                          {trade.symbol}
                        </span>
                      </td>
                      {rowVisibleColumns.map((key) => (
                        <td
                          key={key}
                          className={`px-3 py-2 text-gray-700 align-middle ${key === 'tags' ? 'max-w-[220px]' : 'whitespace-nowrap'}`}
                        >
                          {renderCell(key)}
                        </td>
                      ))}
                      <td className="px-3 py-2 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end">
                          <TradeListQuickActions trade={trade} onUpdated={fetchTrades} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          // Box (grid) layout when trades exist
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${isDashboardWidget ? 'flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden' : ''}`}
            {...(isDashboardWidget && scrollContainerProps)}
            {...(isDashboardWidget && onScrollGuard && { onWheel: onScrollGuard })}
          >
            {/* Existing Trades */}
            {trades
              .filter((trade) => !effectiveShowOnlyOpen || isTradeOpen(trade))
              .map((trade) => {
                const isOpen = isTradeOpen(trade)

                return (
                  <div
                    key={trade.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-md transition-all"
                    style={{backgroundColor: '#FBFBFB'}}
                  >
                    {/* Main Card - Clickable */}
                    <div
                      onClick={() => setSelectedTrade(trade)}
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      {/* Symbol and Status */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-semibold text-gray-900 text-base mb-1">
                            <span className="inline-flex items-center gap-1.5">
                              {(() => {
                                const marketState = resolveMarketState(trade, symbolMetaByKey, marketStateByKey)
                                if (marketState === null) return null
                                return (
                                  <MarketStatusDot
                                    isOpen={marketState.is_market_open}
                                    title={buildMarketStatusTooltip(t, marketState)}
                                  />
                                )
                              })()}
                              {trade.symbol}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                trade.long_short === 'long'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {trade.long_short === 'long' ? t('tradeList.long') : t('tradeList.short')}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                isOpen
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {isOpen ? t('common:status.open') : t('common:status.closed')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Entry Price and Current Price/Unrealized P&L */}
                      <div className={`mb-3 ${isOpen ? 'grid grid-cols-2 gap-3' : ''}`}>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">{t('tradeList.entry')}</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {formatPriceWithCurrency(trade.entry_price, trade.currency)}
                          </div>
                        </div>
                        {isOpen && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('tradeList.currentPrice')}</div>
                            <div className="text-sm font-semibold text-gray-900">
                              {trade.current_price 
                                ? formatPriceWithCurrency(trade.current_price, trade.currency)
                                : '-'}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Unrealized P&L and Daily Change for Open Trades */}
                      {isOpen && trade.current_price && (
                        <div className="mb-3 grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('tradeList.unrealizedPnL')}</div>
                            <div
                              className={`text-sm font-semibold ${
                                trade.unrealized_pnl && trade.unrealized_pnl >= 0 
                                  ? 'text-green-600' 
                                  : 'text-red-600'
                              }`}
                            >
                              {trade.unrealized_pnl !== null && trade.unrealized_pnl !== undefined
                                ? formatCurrency(trade.unrealized_pnl, trade.currency)
                                : '-'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('tradeList.dailyChange')}</div>
                            <div
                              className={`text-sm font-semibold ${
                                trade.daily_change_percentage && trade.daily_change_percentage >= 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {trade.daily_change_percentage !== null && trade.daily_change_percentage !== undefined
                                ? `${trade.daily_change_percentage >= 0 ? '+' : ''}${trade.daily_change_percentage.toFixed(2).replace('.', ',')}%`
                                : '-'}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Last Updated Timestamp for Open Trades */}
                      {isOpen && trade.current_price_updated_at && (
                        <div className="mb-3">
                          <div className="text-[10px] text-gray-400">
                            Updated: {new Date(trade.current_price_updated_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      )}

                      {/* P&L (if closed) */}
                      {!isOpen && trade.pnl !== null && (
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 mb-1">{t('tradeList.realizedPnL')}</div>
                          <div
                            className={`text-sm font-semibold ${
                              trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(trade.pnl, trade.currency)}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div
                        className="flex items-center justify-center pt-3 border-t border-gray-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TradeListQuickActions trade={trade} onUpdated={fetchTrades} />
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </div>


      {/* Edit table columns modal */}
      {showEditTableModal && editTableModalRows !== null && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            setShowEditTableModal(false)
            setEditTableModalRows(null)
          }}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-semibold text-gray-900">{t('tradeList.editTableTitle')}</h3>
            <p className="mb-1 text-sm text-gray-500">{t('tradeList.selectColumns')}</p>
            <p className="mb-4 text-xs text-gray-400">{t('tradeList.reorderColumnsHint')}</p>
            <DndContext
              sensors={editTableSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleEditTableDragEnd}
            >
              <SortableContext items={editTableModalRows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <div className="-mx-1 mb-6 max-h-[50vh] space-y-0.5 overflow-y-auto overscroll-contain px-1">
                  {editTableModalRows.map((row) => (
                    <SortableEditTableRow
                      key={row.id}
                      row={row}
                      label={columnTitle(row.id)}
                      dragLabel={t('tradeList.dragColumnHandle')}
                      onToggleVisible={toggleEditTableModalRowVisible}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowEditTableModal(false)
                  setEditTableModalRows(null)
                }}
                className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  const next = visibleIdsFromModalRows(editTableModalRows)
                  if (next.length > 0) {
                    setRowVisibleColumns(next)
                    try {
                      localStorage.setItem(ROW_TABLE_STORAGE_KEY, JSON.stringify(next))
                    } catch (_) {}
                  }
                  setShowEditTableModal(false)
                  setEditTableModalRows(null)
                }}
                disabled={visibleIdsFromModalRows(editTableModalRows).length === 0}
                className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('buttons.save', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTrade ? (
        <TradeQuickReviewModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
          onViewFullDetail={(trade, event) => handleNavigateToDetail(trade, event)}
          onEditTrade={(trade) => {
            setSelectedTrade(null)
            navigate(`/app/edit-trade/${trade.id}`)
          }}
        />
      ) : null}
    </>
  )
}
